import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { useState, useEffect, lazy, Suspense } from "react";
import { ConfigProvider } from "antd";
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";
import "./App.css";

import UmamiAnalytics from "@danielgtmn/umami-react";
import { useUmami } from "@danielgtmn/umami-react";

import Nav from "./components/Narbar/Navbar";
import Footer from "./components/Footer/Footer";
import FloatingButton from "./UI/FloatingButton/FloatingButton";
import ScrollToTop from "./components/ScrollToTop/ScrollToTop";
import LanguageLayout from "./i18n/LanguageLayout";

import { getLanguage } from "./i18n/languages";

// import SplashScreen from "./components/SplashScreen/SplashScreen";

import { signInAnonymously } from "./services/auth";
import { supabase } from "./utils/supabaseClient";

// Each page (and the chatbot window) is downloaded only when it's needed
const Home = lazy(() => import("./Screens/Home/Home"));
const About = lazy(() => import("./Screens/About/About"));
const Portfolio = lazy(() => import("./Screens/Portfolio/Portfolio"));
const ResourceCenter = lazy(() => import("./Screens/ResourceCentre/ResourceCentre"));
const ResourceCentreLayout = lazy(() =>
  import("./components/ResourceCentreLayout/ResourceCentreLayout")
);
const SingleArticle = lazy(() => import("./Reuseables/SingleArticle/SingleArticle"));
const Contacts = lazy(() => import("./Screens/Contacts/Contacts"));
const PrivacyPolicy = lazy(() => import("./Screens/PrivacyPolicy/PrivacyPolicy"));
const AIModal = lazy(() => import("./UI/AIModal/AIModal"));

// Scroll-in animations, set up once for the whole site
AOS.init({
  duration: 450,
  offset: 60,
  once: true,
  easing: "ease-out-cubic",
  disable: () =>
    window.matchMedia("(prefers-reduced-motion: reduce)").matches,
});

// The same pages are served at the root (English) and under /:lang
const pageRoutes = (
  <>
    <Route index element={<Home />} />
    <Route path="about" element={<About />} />
    <Route path="portfolio" element={<Portfolio />} />
    <Route path="resource-centre" element={<ResourceCenter />}>
      <Route index element={<ResourceCentreLayout />} />
      <Route path="article/:id" element={<SingleArticle />} />
    </Route>
    <Route path="contacts" element={<Contacts />} />
    <Route path="privacy-policy" element={<PrivacyPolicy />} />
  </>
);

function App() {
  const { i18n } = useTranslation();
  const [hideFabText, setHideFabText] = useState(false);

  /* -------------------- APP LOADING STATE -------------------- */
  const [appLoading, setAppLoading] = useState(true);

  /* -------------------- AI MODAL STATES -------------------- */
  const [modalOpen, setModalOpen] = useState(false);
  const [aiAnswer, setAiAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(false);

  const { track } = useUmami();

  /* -------------------- INITIAL APP BOOTSTRAP -------------------- */
  useEffect(() => {
    const initApp = async () => {
      try {
        // Warm up Supabase session
        await signInAnonymously();

        // Optional artificial delay (smooth UX)
        await new Promise((res) => setTimeout(res, 5000));
      } catch (err) {
        console.error("App init error:", err);
      } finally {
        setAppLoading(false);
      }
    };

    initApp();
  }, []);

  /* -------------------- LOCK SCROLL WHEN MODAL OPEN -------------------- */
  useEffect(() => {
    document.body.style.overflow = modalOpen ? "hidden" : "";
  }, [modalOpen]);

  /* -------------------- HANDLERS -------------------- */
  const handleClick = () => {
    track("Open Chatbot", { button: "floating-button" });
  };

  const handleAskLity = async () => {
    handleClick();
    setAiAnswer("");
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setConversationId(null);
    setAiAnswer("");
  };

  const refreshConversations = () => {
    setRefreshFlag((prev) => !prev);
  };

  const handleSubmitQuestion = async (question) => {
    setLoading(true);
    setAiAnswer("");
    let convId = conversationId;

    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) throw new Error("No authenticated user");

      if (!convId) {
        const { data: conv, error } = await supabase
          .from("conversations")
          .insert([{ user_id: userId, title: question }])
          .select()
          .single();

        if (error) throw error;
        convId = conv.id;
        setConversationId(convId);
      }

      const { error: userMsgError } = await supabase
        .from("messages")
        .insert([{ conversation_id: convId, role: "user", content: question }]);
      if (userMsgError) throw userMsgError;

      const response = await fetch(
        `https://next.locateit.co.ke/api/ask?question=${encodeURIComponent(
          question,
        )}`,
      );
      if (!response.ok) {
        throw new Error(`Ask API responded with ${response.status}`);
      }
      const data = await response.json();
      const answer = data.answer || "No answer received.";

      setAiAnswer(answer);

      const { error: answerMsgError } = await supabase
        .from("messages")
        .insert([
          { conversation_id: convId, role: "assistant", content: answer },
        ]);
      if (answerMsgError) throw answerMsgError;

      refreshConversations();
      return convId;
    } catch (error) {
      console.error("Chat error:", error);
      setAiAnswer("Error fetching answer. Try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY > lastScrollY && currentScrollY > 50) {
        // scrolling down
        setHideFabText(true);
      } else {
        // scrolling up
        setHideFabText(false);
      }

      lastScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* -------------------- SPLASH SCREEN -------------------- */
  if (appLoading) {
    // return <SplashScreen loading={appLoading} />;
  }

  /* -------------------- MAIN APP -------------------- */
  return (
    <Router>
      <ConfigProvider direction={getLanguage(i18n.resolvedLanguage).dir}>
      <div className="app-container">
        <UmamiAnalytics
          url="https://cloud.umami.is"
          websiteId="2d65af99-dda0-4ea4-a6ae-3e3ef484daed"
        />

        <ScrollToTop />
        <Nav />

        <main>
          <Suspense fallback={<div className="page-loading" />}>
          <Routes>
            {/* One route tree for every language (English has no prefix),
                so switching language keeps the page mounted */}
            <Route path="/:lang?" element={<LanguageLayout />}>
              {pageRoutes}
            </Route>
          </Routes>
          </Suspense>
        </main>

        <FloatingButton onClick={handleAskLity} hideText={hideFabText} />

        {modalOpen && (
          <Suspense fallback={null}>
        <AIModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          onSubmit={handleSubmitQuestion}
          loading={loading}
          aiAnswer={aiAnswer}
          conversationId={conversationId}
          setConversationId={setConversationId}
          refreshConversations={refreshFlag}
        />
          </Suspense>
        )}

        <Footer />
      </div>
      </ConfigProvider>
    </Router>
  );
}

export default App;
